"use client"
import React, { useState } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// Sample product data
const products = [
    {
        id: 1,
        title: 'بازی کال اف دیویتی',
        subtitle: 'سری جدید ۲۰۲۴',
        price: '۱۲,۵۰۰,۰۰۰',
        discount: '۱۵',
        image: '/gallery/2.png',
        badge: 'پیشنهاد ویژه',
        color: 'to-[#16a34a]'
    },
    {
        id: 2,
        title: 'دسته بازی',
        subtitle: 'متن تست',
        price: '۸,۹۰۰,۰۰۰',
        discount: '۲۰',
        image: '/gallery/3.png',
        badge: 'پرفروش',
        color: 'to-[#2563eb]'
    },
    {
        id: 3,
        title: 'سی دی کی بازی',
        subtitle: 'کیت کامل',
        price: '۱۵,۹۰۰,۰۰۰',
        discount: '۱۰',
        image: '/gallery/4.png',
        badge: 'موجود',
        color: 'to-[#ff6b00]'
    },

    {
        id: 4,
        title: 'سی دی کی بازی',
        subtitle: 'کیت کامل',
        price: '۱۵,۹۰۰,۰۰۰',
        discount: '۱۰',
        image: '/gallery/7.png',
        badge: 'موجود',
        color: 'to-[#ff6b00]'
    },

    {
        id: 5,
        title: 'سی دی کی بازی',
        subtitle: 'کیت کامل',
        price: '۱۵,۹۰۰,۰۰۰',
        discount: '۱۰',
        image: '/gallery/9.jpg',
        badge: 'موجود',
        color: 'to-[#ff6b00]'
    }
];

export default function ProductSlider() {
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <div className="relative py-4 mx-auto z-0 px-10">
            {/* Background Container */}
            <div className='absolute overflow-hidden inset-0 w-full h-full bg-[#19161D] border-[1px] border-[#2A2633] z-10 rounded-[32px]' />

            {/* Side Decorations */}
            <div className='absolute inset-0 w-full h-full z-20 overflow-hidden'>
                <div className='h-full flex justify-between items-center'>
                    <svg className='transform -rotate-90 translate-x-[94px] w-48 z-10' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 423.11 61.82">
                        <g id="Layer_1-2" data-name="Layer 1">
                            <path
                                stroke="#2A2633"
                                strokeWidth="3"
                                strokeLinecap="round"
                                fill="#0A0512"
                                d="m423.11,59.53c-18.3.03-45.42-1.52-76.73-9.83C276.69,31.22,269.75,1.46,226.9.05c-48.74-1.6-59.97,36.25-131.37,54.31C56.28,64.28,21.96,62.38,0,59.53"
                            />
                        </g>
                    </svg>

                    <svg className='transform -rotate-90 -translate-x-[94px] w-48 z-10' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 423.11 61.82">
                        <g id="Layer_1-2" data-name="Layer 1">
                            <path
                                stroke="#2A2633"
                                strokeWidth="3"
                                strokeLinecap="round"
                                fill="#0A0512"
                                d="m423.11,2.29c-18.3-.03-45.42,1.52-76.73,9.83-69.68,18.48-76.63,48.24-119.48,49.65-48.74,1.6-59.97-36.25-131.37-54.31C56.28-2.46,21.96-.57,0,2.29"
                            />
                        </g>
                    </svg>
                </div>
            </div>

            {/* Bottom Curve */}
            <div className='absolute inset-0 flex justify-center items-end z-10 overflow-hidden'>
                <svg className='w-48 mx-auto transform translate-y-[48px] z-10' xmlns="http://www.w3.org/2000/svg" width="450" height="100" viewBox="0 0 450 100">
                    <path
                        d="M0,55 C50,55 50,20 100,20 H350 C400,20 400,55 450,55"
                        fill="#0A0512"
                        stroke="#2A2633"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                </svg>
            </div>

            {/* Navigation Buttons */}
            <div className="absolute inset-0 w-full h-full z-20">
                <div className='swiper-button-prev swiper-button-next !h-8 after:!text-sm !text-white transform translate-x-[24px]' />
                <div className='swiper-button-prev !h-8 after:!text-sm !text-white transform -translate-x-[24px]' />
            </div>

            {/* Main Slider Content */}
            <div className='relative z-10 flex flex-col gap-2'>
                <div className='flex-1 h-[300px] min-h-[300px]'>
                    <Swiper
                        modules={[Pagination, Autoplay, Navigation]}
                        slidesPerView={1}
                        pagination={{
                            el: '.swiper-pagination',
                            clickable: true,
                            renderBullet: (_, className) => {
                                return `<span class="${className} !w-2 !h-2 !bg-[hsl(var(--primary))] !opacity-50 hover:!opacity-75 transition-opacity"></span>`;
                            },
                        }}
                        navigation={{
                            prevEl: '.swiper-button-prev',
                            nextEl: '.swiper-button-next',
                        }}
                        autoplay={{ delay: 5000 }}
                        loop={true}
                        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                        className="w-full h-full group"
                    >
                        {products.map((product) => (
                            <SwiperSlide key={product.id}>
                                <div className="relative w-full h-full overflow-hidden rounded-3xl px-4 pt-4 pb-6">
                                    {/* Slide Background */}
                                    <div className={cn(
                                        "absolute inset-0 opacity-20 blur-3xl bg-gradient-to-r from-transparent",
                                        product.color
                                    )} />

                                    {/* Slide Content */}
                                    <div className="relative h-full flex md:flex-row flex-col items-center gap-5 justify-between">
                                        {/* Text Content */}
                                        <div className="flex md:flex-col flex-row justify-between w-full space-y-6 items-start md:max-w-[60%]">
                                            <div className="space-y-3">
                                                <span className="inline-block px-2 md:px-3 py-1 text-xs md:text-sm rounded-full bg-primary/20 text-white">
                                                    {product.badge}
                                                </span>

                                                <div>
                                                    <h2 className="md:text-3xl text-lg font-bold text-white">
                                                        {product.title}
                                                    </h2>
                                                    <p className="text-white/60 md:text-lg text-xs mt-1">
                                                        {product.subtitle}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Price Section */}
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="md:text-2xl text-lg font-bold text-white">
                                                            {product.price}
                                                        </span>
                                                        <span className="text-white/60">تومان</span>
                                                    </div>
                                                    {product.discount && (
                                                        <span className="md:text-sm text-xs md:px-2 px-1 py-0.5 rounded bg-primary/20 text-white">
                                                            {product.discount}٪ تخفیف
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Image Section */}
                                        <div className="relative w-[350px] md:h-[250px] h-[180px]">
                                            <div className="absolute inset-0 rounded-2xl overflow-hidden border border-white/10">
                                                <Image
                                                    src={product.image}
                                                    alt={product.title}
                                                    fill
                                                    className="object-cover transform group-hover:scale-105 transition-transform duration-500"
                                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                />
                                            </div>
                                            <div className="absolute -bottom-2 -right-2 w-full h-full rounded-2xl border border-white/20 -z-10" />
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* Pagination Dots */}
                <div className="flex z-40 gap-2 justify-center items-center absolute inset-x-0 bottom-0 transform translate-y-[19px]">
                    {products.map((_, index) => (
                        <span
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeIndex
                                    ? 'bg-[hsl(var(--primary))] w-5'
                                    : 'bg-[hsl(var(--primary)/30%)]'
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
} 